// Render fragment for of — the entity's GraphQL display contract.
// Scaffolded once from the @medplum/definitions reverse projection; hand-curate
// freely to match what the view renders (this file is owned, not generated).
import { graphql } from '@eluve/graphql-fhir';

/**
 * Everything a MedicationRequest view (or the generic fallback) could render.
 * Compose into queries: graphql(`query { ... ...MedicationRequestRender }`, [MedicationRequestRenderFragment]).
 * References are terminal ({ reference type display }) — expand nested
 * `resource` selections in the query when resolved targets are needed.
 */
export const MedicationRequestRenderFragment = graphql(`
  fragment MedicationRequestRender on MedicationRequest @_unmask {
    resourceType
    id
    meta {
      versionId
      lastUpdated
      profile
      tag {
        system
        code
      }
      security {
        system
        code
        display
      }
    }
    modifierExtension {
      url
    }
    identifier {
      use
      system
      value
    }
    status
    statusReason {
      text
      coding {
        system
        code
        display
      }
    }
    intent
    category {
      text
      coding {
        system
        code
        display
      }
    }
    priority
    doNotPerform
    reportedBoolean
    reportedReference {
      reference
      type
      display
    }
    medicationCodeableConcept {
      text
      coding {
        system
        code
        display
      }
    }
    medicationReference {
      reference
      type
      display
    }
    subject {
      reference
      type
      display
    }
    encounter {
      reference
      type
      display
    }
    supportingInformation {
      reference
      type
      display
    }
    authoredOn
    requester {
      reference
      type
      display
    }
    performer {
      reference
      type
      display
    }
    performerType {
      text
      coding {
        system
        code
        display
      }
    }
    recorder {
      reference
      type
      display
    }
    reasonCode {
      text
      coding {
        system
        code
        display
      }
    }
    reasonReference {
      reference
      type
      display
    }
    instantiatesCanonical
    instantiatesUri
    basedOn {
      reference
      type
      display
    }
    groupIdentifier {
      use
      system
      value
    }
    courseOfTherapyType {
      text
      coding {
        system
        code
        display
      }
    }
    insurance {
      reference
      type
      display
    }
    note {
      authorReference {
        reference
        type
        display
      }
      authorString
      time
      text
    }
    dosageInstruction {
      sequence
      text
      additionalInstruction {
        text
        coding {
          system
          code
          display
        }
      }
      patientInstruction
      timing {
        event
        repeat {
          boundsPeriod {
            start
            end
          }
          count
          countMax
          frequency
          frequencyMax
          period
          periodUnit
          dayOfWeek
          timeOfDay
          when
          offset
        }
        code {
          text
          coding {
            system
            code
            display
          }
        }
      }
      asNeededBoolean
      asNeededCodeableConcept {
        text
        coding {
          system
          code
          display
        }
      }
      site {
        text
        coding {
          system
          code
          display
        }
      }
      route {
        text
        coding {
          system
          code
          display
        }
      }
      method {
        text
        coding {
          system
          code
          display
        }
      }
      doseAndRate {
        type {
          text
          coding {
            system
            code
            display
          }
        }
        doseRange {
          low {
            value
            comparator
            unit
            system
            code
          }
          high {
            value
            comparator
            unit
            system
            code
          }
        }
        doseQuantity {
          value
          comparator
          unit
          system
          code
        }
        rateRatio {
          numerator {
            value
            comparator
            unit
            system
            code
          }
          denominator {
            value
            comparator
            unit
            system
            code
          }
        }
        rateRange {
          low {
            value
            comparator
            unit
            system
            code
          }
          high {
            value
            comparator
            unit
            system
            code
          }
        }
        rateQuantity {
          value
          comparator
          unit
          system
          code
        }
      }
      maxDosePerPeriod {
        numerator {
          value
          comparator
          unit
          system
          code
        }
        denominator {
          value
          comparator
          unit
          system
          code
        }
      }
      maxDosePerAdministration {
        value
        comparator
        unit
        system
        code
      }
      maxDosePerLifetime {
        value
        comparator
        unit
        system
        code
      }
    }
    dispenseRequest {
      initialFill {
        quantity {
          value
          comparator
          unit
          system
          code
        }
        duration {
          value
          comparator
          unit
          system
          code
        }
      }
      dispenseInterval {
        value
        comparator
        unit
        system
        code
      }
      validityPeriod {
        start
        end
      }
      numberOfRepeatsAllowed
      quantity {
        value
        comparator
        unit
        system
        code
      }
      expectedSupplyDuration {
        value
        comparator
        unit
        system
        code
      }
      performer {
        reference
        type
        display
      }
    }
    substitution {
      allowedBoolean
      allowedCodeableConcept {
        text
        coding {
          system
          code
          display
        }
      }
      reason {
        text
        coding {
          system
          code
          display
        }
      }
    }
    priorPrescription {
      reference
      type
      display
    }
    detectedIssue {
      reference
      type
      display
    }
    eventHistory {
      reference
      type
      display
    }
  }
`);
